import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import {
  Notification,
  isUnread,
  metaFor,
  routeFor,
  relativeTime,
} from '../lib/notifications';
import { NotificationTypeIcon } from './NotificationTypeIcon';

/**
 * NotificationBell — the header bell for the top app bar.
 *
 * A bell button with an unread-count badge + a body-portaled dropdown of the
 * recent (~last two weeks) notifications. Each row deep-links to its entity and
 * marks itself read; "Mark all read" clears the badge; "View all" opens the full
 * Notifications page. Polls every 60s so the badge stays live without a reload.
 *
 * Reads the SAME self-scoped feed (GET /api/my-notifications) as the Notifications
 * page + the dashboard, sharing lib/notifications for the row shape + helpers. The
 * dropdown is portaled to <body> to escape the sticky header's overflow/z-index
 * (mirrors ViewAsMenu).
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number; width: number; maxHeight: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const query = useQuery<Notification[]>({
    queryKey: ['notifications-bell'],
    queryFn: ({ signal }) => api.get<Notification[]>('/api/my-notifications?days=14&limit=20', signal),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ['notifications-bell'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-dashboard'] });
  }

  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/api/notifications/${id}/read`),
    onSuccess: invalidateAll,
  });
  const markAll = useMutation({
    mutationFn: () => api.post('/api/notifications/read-all'),
    onSuccess: invalidateAll,
  });

  const all = query.data ?? [];
  const unread = useMemo(() => all.filter((n) => isUnread(n)).length, [all]);
  const recent = useMemo(
    () => [...all].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')).slice(0, 8),
    [all],
  );

  // Position the portaled dropdown + close on outside click / Escape.
  useEffect(() => {
    if (!open) { setMenuPos(null); return; }
    const place = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (!r) return;
      const gutter = 8;
      const top = r.bottom + 6;
      // On a phone (≤ sm breakpoint) the dropdown becomes a near-full-screen
      // sheet: full width bar the side gutters, and as tall as the viewport
      // allows (no desktop 480 cap) so it reads as a real panel, not a cramped
      // ~5/6-wide, ~2/3-tall box in the corner.
      if (window.innerWidth <= 640) {
        const width = window.innerWidth - gutter * 2;
        setMenuPos({ top, right: gutter, width, maxHeight: window.innerHeight - top - gutter });
        return;
      }
      // Desktop / tablet: a 360-wide panel anchored under the bell, then CLAMPED
      // so it stays fully on-screen — its left edge never goes negative and its
      // right edge keeps an 8px gutter.
      const width = Math.min(360, window.innerWidth - 16);
      const desiredRight = window.innerWidth - r.right;
      const right = Math.min(Math.max(gutter, desiredRight), Math.max(gutter, window.innerWidth - width - gutter));
      setMenuPos({ top, right, width, maxHeight: Math.min(480, window.innerHeight - top - 16) });
    };
    place();
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  function openNotification(n: Notification) {
    if (isUnread(n)) markRead.mutate(n.id);
    setOpen(false);
    const route = routeFor(n);
    if (route) navigate(route);
    else navigate('/notifications');
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        title="Notifications"
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          flexShrink: 0,
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-md)',
          background: 'var(--color-card)',
          color: 'var(--color-ink-2)',
          cursor: 'pointer',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -5,
              right: -5,
              minWidth: 17,
              height: 17,
              padding: '0 4px',
              borderRadius: 999,
              background: 'var(--color-status-red)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 'var(--fw-semibold)',
              lineHeight: '17px',
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: menuPos.top,
            right: menuPos.right,
            zIndex: 1000,
            width: menuPos.width,
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: menuPos.maxHeight,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-lg)',
            boxShadow: '0 8px 24px rgba(15,23,24,0.16)',
            overflow: 'hidden',
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--sp-3)',
            borderBottom: '1px solid var(--color-border)',
          }}>
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
              Notifications{unread > 0 ? ` · ${unread} new` : ''}
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                style={{
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  color: 'var(--brand-primary)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)',
                  fontFamily: 'inherit', padding: 0,
                }}
              >
                {markAll.isPending ? 'Marking…' : 'Mark all read'}
              </button>
            )}
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {query.isLoading && (
              <div style={{ padding: 'var(--sp-4)', color: 'var(--color-ink-2)', fontSize: 'var(--fs-sm)' }}>Loading…</div>
            )}
            {!query.isLoading && recent.length === 0 && (
              <div style={{ padding: 'var(--sp-5)', color: 'var(--color-muted)', fontSize: 'var(--fs-sm)', textAlign: 'center' }}>
                You're all caught up.
              </div>
            )}
            {recent.map((n) => {
              const meta = metaFor(n.trigger_type);
              const unreadRow = isUnread(n);
              return (
                <button
                  key={n.id}
                  type="button"
                  role="menuitem"
                  onClick={() => openNotification(n)}
                  style={{
                    display: 'flex',
                    gap: 'var(--sp-2)',
                    width: '100%',
                    textAlign: 'left',
                    padding: 'var(--sp-3)',
                    border: 'none',
                    borderBottom: '1px solid var(--color-border)',
                    background: unreadRow ? 'var(--brand-primary-bg)' : 'transparent',
                    color: 'var(--color-ink)',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = unreadRow ? 'var(--brand-primary-bg)' : 'transparent'; }}
                >
                  <span aria-hidden style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 22, color: 'var(--color-ink-3)' }}><NotificationTypeIcon type={n.trigger_type} size={16} /></span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      display: 'block',
                      fontSize: 'var(--fs-sm)',
                      fontWeight: unreadRow ? 'var(--fw-semibold)' : 'var(--fw-regular, 400)',
                      color: 'var(--color-ink)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>{n.title || meta.label}</span>
                    <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
                      {meta.label} · {relativeTime(n.created_at)}
                    </span>
                  </span>
                  {unreadRow && (
                    <span aria-hidden style={{ flexShrink: 0, width: 8, height: 8, borderRadius: 999, background: 'var(--brand-primary)', marginTop: 6 }} />
                  )}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/notifications'); }}
            style={{
              padding: 'var(--sp-3)',
              border: 'none',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-card)',
              color: 'var(--brand-primary)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 'var(--fw-medium)',
              fontFamily: 'inherit',
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            View all notifications
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}
